import { useEffect, useId, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { createPortal } from "react-dom";
import { normalizeSelectOptions } from "../utils/selectOptions";
import { normalizeSearchText as normalizeSearch } from "../utils/normalizeSearchText";

export default function SearchableSelect({ value, options, onChange, placeholder = "Select...", searchPlaceholder = "Search...", ariaLabel = "Choose an option", disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const [position, setPosition] = useState(null);
  const trigger = useRef(null);
  const menu = useRef(null);
  const search = useRef(null);
  const optionRefs = useRef([]);
  const id = useId();
  const reducedMotion = useReducedMotion();
  const positioned = Boolean(position);
  const currentValue = String(value ?? "");
  const normalized = useMemo(() => normalizeSelectOptions(options), [options]);
  const selected = normalized.find((option) => option.value === currentValue);
  const filtered = useMemo(() => {
    const tokens = normalizeSearch(query).split(/\s+/).filter(Boolean);
    return normalized.filter((option) => tokens.every((token) => normalizeSearch(`${option.label} ${option.value} ${option.searchText}`).includes(token)));
  }, [normalized, query]);

  const close = (restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) trigger.current?.focus();
  };
  const choose = (option) => {
    if (!option || option.disabled) return;
    onChange(option.value);
    close();
  };

  useEffect(() => {
    if (!open) return;
    const update = (event) => {
      if (event?.target instanceof Node && menu.current?.contains(event.target)) return;
      const rect = trigger.current?.getBoundingClientRect();
      if (!rect) return;
      const gutter = 12;
      const gap = 8;
      const below = window.innerHeight - rect.bottom - gap - gutter;
      const above = rect.top - gap - gutter;
      const upward = below < 240 && above > below;
      const width = Math.min(Math.max(rect.width, 240), window.innerWidth - gutter * 2);
      setPosition({
        left: Math.max(gutter, Math.min(rect.left, window.innerWidth - gutter - width)),
        width,
        maxHeight: Math.max(120, Math.min(360, upward ? above : below)),
        ...(upward ? { bottom: window.innerHeight - rect.top + gap } : { top: rect.bottom + gap }),
      });
    };
    const outside = (event) => {
      if (!trigger.current?.contains(event.target) && !menu.current?.contains(event.target)) setOpen(false);
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    document.addEventListener("pointerdown", outside);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      document.removeEventListener("pointerdown", outside);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !positioned) return;
    const frame = requestAnimationFrame(() => search.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open, positioned]);

  useEffect(() => { optionRefs.current[highlighted]?.scrollIntoView?.({ block: "nearest" }); }, [highlighted]);

  const openMenu = () => {
    setPosition(null);
    setQuery("");
    setHighlighted(Math.max(0, normalized.findIndex((option) => option.value === currentValue)));
    setOpen(true);
  };
  const keyDown = (event) => {
    if (event.key === "Escape") { event.preventDefault(); close(); }
    else if (event.key === "Tab") close();
    else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      setHighlighted((current) => filtered.length ? (current + direction + filtered.length) % filtered.length : 0);
    } else if (event.key === "Enter") { event.preventDefault(); choose(filtered[highlighted]); }
  };

  return (
    <div className="ib-select-wrap">
      <button ref={trigger} className="ib-select-trigger" type="button" disabled={disabled} aria-label={ariaLabel} aria-describedby={`${id}-value`} title={selected?.label || placeholder} aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => open ? close() : openMenu()} onKeyDown={(event) => {
        if (["ArrowDown", "ArrowUp"].includes(event.key)) { event.preventDefault(); openMenu(); }
        if (event.key === "Escape" && open) close();
      }}>
        <span id={`${id}-value`}>{selected?.label || value || placeholder}</span>
        <motion.svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" animate={{ rotate: open ? 180 : 0 }} transition={{ duration: reducedMotion ? 0 : 0.18 }}><path d="m6 9 6 6 6-6" /></motion.svg>
      </button>
      {typeof document !== "undefined" && createPortal(
        <AnimatePresence>
          {open && position && <motion.div key="menu" ref={menu} className="ib-select-menu" style={position} initial={reducedMotion ? false : { opacity: 0, y: -6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, transition: { duration: reducedMotion ? 0 : 0.15 } }} transition={{ duration: reducedMotion ? 0 : 0.18 }} onKeyDown={keyDown}>
            <div className="ib-select-search"><input ref={search} className="ib-field" role="combobox" aria-label={`Search ${ariaLabel}`} aria-controls={id} aria-expanded="true" aria-autocomplete="list" aria-activedescendant={filtered[highlighted] ? `${id}-${highlighted}` : undefined} placeholder={searchPlaceholder} value={query} onChange={(event) => { setQuery(event.target.value); setHighlighted(0); }} /></div>
            <div id={id} className="ib-select-options" role="listbox" aria-label={ariaLabel} style={{ maxHeight: Math.max(60, position.maxHeight - 76) }}>
              {filtered.length ? filtered.map((option, index) => <button key={option.value} id={`${id}-${index}`} ref={(node) => { optionRefs.current[index] = node; }} className={`ib-select-option${highlighted === index ? " is-highlighted" : ""}`} type="button" role="option" tabIndex={-1} aria-selected={option.value === currentValue} disabled={option.disabled} onPointerMove={() => setHighlighted(index)} onClick={() => choose(option)}>
                <span>{option.label}</span>
                {option.value === currentValue && <svg className="ib-select-option-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>}
              </button>) : <p className="ib-select-empty" role="status">No matches</p>}
            </div>
          </motion.div>}
        </AnimatePresence>, document.body)}
    </div>
  );
}
